/**
 * Theme Apply Groups
 *
 * Derives selective-apply attribute groups (Background / Header / Footer / Body)
 * from V2 mapping target paths. The first dot/bracket segment of a mapping's
 * `target` identifies the StepContent region it touches, so groups are computed
 * at runtime with zero changes to the mapping tables.
 *
 * Used by `applyThemeGroupsToScreens` to push chosen parts of a theme onto
 * live canvas screens.
 *
 * @module themes/mappers
 */

import type { StepKind } from '@segmently/schema-models/v2';
import type { ThemeV2Mapping } from './ThemeV2Mapper';

export type ThemeApplyGroup = 'background' | 'header' | 'footer' | 'body' | 'animations';

export const ALL_THEME_APPLY_GROUPS: readonly ThemeApplyGroup[] = [
  'background',
  'header',
  'footer',
  'body',
  'animations',
];

export const THEME_APPLY_GROUP_LABELS: Record<ThemeApplyGroup, string> = {
  background: 'Background',
  header: 'Header',
  footer: 'Footer',
  body: 'Body',
  animations: 'Animations',
};

/**
 * Body covers every token-mapped region that is not the header or action bar.
 * Background contains the structural backdrop override plus the optional
 * `canvas.webViewBackgroundColor` mapping.
 */
const BODY_TARGET_PREFIXES = new Set([
  'copy',
  'options',
  'carousel',
  'stepper',
  'paywall',
  'textField',
  'roller',
  'featuredMedia',
  'flexibleLayout',
]);

const warnedPrefixes = new Set<string>();

/**
 * Resolve the apply group for a mapping from its target-path prefix.
 *
 * Fail-closed: an unknown prefix returns `null` (the mapping is excluded from
 * every group) and logs a one-time warning, so a future un-grouped mapping
 * can never silently leak into a selective apply.
 */
export function getMappingGroup(mapping: ThemeV2Mapping): ThemeApplyGroup | null {
  if (mapping.target === 'canvas.webViewBackgroundColor') return 'background';
  const prefix = mapping.target.split(/[.\[]/, 1)[0];
  if (prefix === 'header') return 'header';
  if (prefix === 'actionBar') return 'footer';
  if (BODY_TARGET_PREFIXES.has(prefix)) return 'body';
  if (!warnedPrefixes.has(prefix)) {
    warnedPrefixes.add(prefix);
    console.warn(
      `[themeApplyGroups] Unknown mapping target prefix "${prefix}" — mapping excluded from all apply groups`
    );
  }
  return null;
}

/**
 * Which groups can be applied to a given StepKind.
 *
 * Mirrors the header/actionBar exclusion in
 * `DefaultThemeV2Mapper.applyToFlowDocument`: Paywall, WebEmbed and
 * FlexibleLayout manage their own header/actionBar. Paywall's CTA and product
 * cells are `paywall.*` (Body); FlexibleLayout's geometry is
 * `flexibleLayout.*` (Body); WebEmbed has no themable body tokens.
 *
 * Animations (screen entrance — `content.screenAnimation`/`animated`, verbatim
 * template copy) apply to every kind except WebEmbed: custom HTML owns its own
 * motion (mirrors `applyAnimationSettingsToFlowDocument`).
 */
export function supportedGroups(stepKind: StepKind): ThemeApplyGroup[] {
  switch (stepKind) {
    case 'Paywall':
    case 'FlexibleLayout':
      return ['background', 'body', 'animations'];
    case 'WebEmbed':
      return ['background'];
    default:
      return [...ALL_THEME_APPLY_GROUPS];
  }
}
